/**
 * Marketplace System Settings — labels/defaults for admin UI (Wave 1.5).
 * Shown even when DB rows are missing (legacy envs that haven't re-seeded).
 * @see backend/scripts/migration/seed-system-settings.js
 */

/**
 * @typedef {object} MarketplaceFieldMeta
 * @property {string} label
 * @property {"features"|"limits"|"integrations"} category
 * @property {"experiment"|"leadTime"|"opsAlerts"|"providerOnboarding"} section
 * @property {"boolean"|"string"|"number"|"object"} type
 * @property {string} description
 * @property {*} defaultValue
 * @property {object} [metadata]
 */

/** @type {readonly { key: string, title: string, hint?: string }[]} */
export const MARKETPLACE_SECTIONS = Object.freeze([
  {
    key: "experiment",
    title: "Experiment cohort & confidence gates",
    hint: "Controls when ProviderOpportunity auto-create runs after deposit verification and when the Wave 1.5 confidence sample is considered met.",
  },
  {
    key: "leadTime",
    title: "Selection lead times",
    hint: "How many hours before collection the provider selection window closes — shared vs dedicated/exclusive loads.",
  },
  {
    key: "opsAlerts",
    title: "Ops notifications",
    hint: "Email and in-app alerts when providers respond or when a selection deadline is approaching.",
  },
  {
    key: "providerOnboarding",
    title: "Provider onboarding",
    hint: "Wave 1.6 opt-in intake: paste the Google Form URL here (no redeploy). Prefill map keys match intake field names → entry.XXX ids. Keep the onboarding gate off until staging is verified.",
  },
]);

/** @type {Record<string, MarketplaceFieldMeta>} */
export const MARKETPLACE_FIELD_META = Object.freeze({
  "marketplace.experiment.startAt": {
    label: "Experiment cohort start (ISO)",
    category: "features",
    section: "experiment",
    type: "string",
    defaultValue: "",
    description:
      "Required for post-deposit ProviderOpportunity auto-create. ISO UTC timestamp when the experiment cohort opens (empty = cohort closed — no auto-create). Example: 2026-08-08T12:00:00.000Z. Seed default is empty until ops sets a go-live time.",
  },
  "marketplace.experiment.minFundedOpportunities": {
    label: "Min funded opportunities (confidence)",
    category: "features",
    section: "experiment",
    type: "number",
    defaultValue: 100,
    description:
      "Minimum funded ProviderOpportunities before Wave 1.5 confidence sample is met",
    metadata: { min: 1, unit: "count" },
  },
  "marketplace.experiment.minCompletedMoves": {
    label: "Min completed moves (confidence)",
    category: "features",
    section: "experiment",
    type: "number",
    defaultValue: 70,
    description:
      "Minimum fulfilled ProviderOpportunities before Wave 1.5 confidence sample is met",
    metadata: { min: 1, unit: "count" },
  },
  "marketplace.leadTime.sharedLoadHours": {
    label: "Shared-load selection close (hours)",
    category: "limits",
    section: "leadTime",
    type: "number",
    defaultValue: 48,
    description:
      "Hours before collection when shared-load ProviderOpportunity selection closes",
    metadata: { min: 1, max: 720, unit: "hours" },
  },
  "marketplace.leadTime.dedicatedLoadHours": {
    label: "Dedicated-load selection close (hours)",
    category: "limits",
    section: "leadTime",
    type: "number",
    defaultValue: 72,
    description:
      "Hours before collection when dedicated/exclusive-load ProviderOpportunity selection closes",
    metadata: { min: 1, max: 720, unit: "hours" },
  },
  "marketplace.ops.notifyOnInterest": {
    label: "Email admin when provider responds",
    category: "features",
    section: "opsAlerts",
    type: "boolean",
    defaultValue: true,
    description:
      "Send admin email and in-app notification when a provider accepts or declines a marketplace job",
  },
  "marketplace.ops.deadlineWarningHours": {
    label: "Deadline warning lead time (hours)",
    category: "features",
    section: "opsAlerts",
    type: "number",
    defaultValue: 24,
    description:
      "Hours before ProviderOpportunity selectionDeadlineAt to email admins a selection deadline warning",
    metadata: { min: 1, max: 168, unit: "hours" },
  },
  "marketplace.providerOnboarding.enabled": {
    label: "Provider onboarding gate enabled",
    category: "features",
    section: "providerOnboarding",
    type: "boolean",
    defaultValue: false,
    description:
      "When true, providers must be vetted and e-sign before job access. Keep false until staging verify.",
  },
  "marketplace.providerApply.googleFormUrl": {
    label: "Provider apply Google Form URL",
    category: "integrations",
    section: "providerOnboarding",
    type: "string",
    defaultValue: "",
    description:
      "Paste the public Google Form link here (ops). Intake follow-up email uses this URL; leave empty until the form is ready. Never hardcode a docs.google.com URL in code.",
  },
  "marketplace.providerApply.googleFormPrefill": {
    label: "Provider apply Google Form prefill map",
    category: "integrations",
    section: "providerOnboarding",
    type: "object",
    defaultValue: {},
    description:
      'JSON object mapping intake fields to Google Form entry.XXX ids, e.g. {"email":"entry.123","idDocumentType":"entry.456","driversSummary":"entry.789"}',
  },
});

/** @type {readonly string[]} */
export const MARKETPLACE_SETTING_KEYS = Object.freeze(
  Object.keys(MARKETPLACE_FIELD_META)
);

/**
 * @param {string} key
 * @returns {MarketplaceFieldMeta|undefined}
 */
export const getMarketplaceFieldMeta = (key) => MARKETPLACE_FIELD_META[key];

/**
 * @param {string} key
 * @returns {boolean}
 */
export const isMarketplaceSettingKey = (key) =>
  typeof key === "string" && key.startsWith("marketplace.");

/**
 * Synthetic setting rows for keys missing from the API response.
 * @param {Array<{key: string}>} existingSettings
 * @returns {Array<object>}
 */
export const getMissingMarketplaceSettings = (existingSettings = []) => {
  const existing = new Set(existingSettings.map((s) => s.key));
  return MARKETPLACE_SETTING_KEYS.filter((key) => !existing.has(key)).map(
    (key) => {
      const meta = MARKETPLACE_FIELD_META[key];
      return {
        key,
        value: meta.defaultValue,
        type: meta.type,
        scope: "private",
        category: meta.category,
        description: meta.description,
        metadata: meta.metadata || {},
        _synthetic: true,
      };
    }
  );
};
